import React, {useEffect, useState} from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AddIcon from '@mui/icons-material/Add'
import KeyOffOutlinedIcon from '@mui/icons-material/KeyOffOutlined'
import Loader from 'shared/components/atoms/Loader'
import ConfirmationModal from '../ConfirmationModal'
import {API} from 'aws-amplify'
import {Toast} from '../../toast/toast'

interface ApiKey {
    id: string
    is_active: boolean
    generated_by: string
    last_used: string | null
    revoked_at: string | null
    revoked_by: string | null
    create_time: string
    update_time: string
    corp_code: string
    scope: 'read' | 'read_write'
    api_key_preview: string
}

interface GenerateApiKeyResponse {
    corp_code: string
    scope: string
    api_key_preview: string
    api_key: string
    message: string
}

const font = {
    regular: 'PlusJakartaSansRegular',
    medium: 'PlusJakartaSansMedium',
    semibold: 'PlusJakartaSansSemiBold',
    bold: 'PlusJakartaSansBold',
    mono: 'Courier New, monospace',
}

const ApiKeysManagement: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
    const [corpCodes, setCorpCodes] = useState<string[]>([])
    const [includeRevoked, setIncludeRevoked] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isCorpCodesLoading, setIsCorpCodesLoading] = useState(true)

    const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
    const [generateCorpCode, setGenerateCorpCode] = useState<string | null>(null)
    const [generateScope, setGenerateScope] = useState('read')
    const [isGenerating, setIsGenerating] = useState(false)
    const [generatedKey, setGeneratedKey] = useState<GenerateApiKeyResponse | null>(null)
    const [keyCopied, setKeyCopied] = useState(false)

    const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null)
    const [revokeKeyPreview, setRevokeKeyPreview] = useState<string>('')
    const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false)

    const [sortField, setSortField] = useState<'scope' | 'status' | 'created' | 'last_used' | null>(null)
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

    useEffect(() => {
        fetchCorpCodes()
        fetchApiKeys()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchCorpCodes = async () => {
        setIsCorpCodesLoading(true)
        try {
            const response: string[] = await API.get('CorpOnboarding', '/looker-corp-codes', {})
            setCorpCodes(response)
        } catch (error) {
            console.error('Error fetching corp codes:', error)
            Toast.show({type: 'error', text1: 'Failed to fetch corporate codes.'})
        } finally {
            setIsCorpCodesLoading(false)
        }
    }

    const fetchApiKeys = async () => {
        setIsLoading(true)
        try {
            const response = await API.get('CorpOnboarding', `/partner-api-keys?include_revoked=${includeRevoked ? 'true' : 'false'}`, {})
            setApiKeys(response)
        } catch (error) {
            console.error('Error fetching API keys:', error)
            Toast.show({type: 'error', text1: 'Failed to fetch API keys.'})
            setApiKeys([])
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchApiKeys()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeRevoked])

    const handleGenerateKey = async () => {
        if (!generateCorpCode) {
            Toast.show({type: 'error', text1: 'Please select a corporate code.'})
            return
        }
        setIsGenerating(true)
        try {
            const response: GenerateApiKeyResponse = await API.post('CorpOnboarding', '/partner-api-key/generate', {
                body: {corp_code: generateCorpCode, scope: generateScope},
            })
            setGeneratedKey(response)
            Toast.show({type: 'success', text1: 'API key generated successfully.'})
            fetchApiKeys()
        } catch (error: any) {
            const errorMessage = error?.response?.data?.detail || 'Failed to generate API key. Please try again.'
            console.error('Error generating API key:', error)
            Toast.show({type: 'error', text1: errorMessage})
        } finally {
            setIsGenerating(false)
        }
    }

    const handleCloseGenerateDialog = () => {
        setGenerateDialogOpen(false)
        setGenerateCorpCode(null)
        setGenerateScope('read')
        setGeneratedKey(null)
        setKeyCopied(false)
    }

    const handleCopyKey = async () => {
        if (generatedKey?.api_key) {
            try {
                await navigator.clipboard.writeText(generatedKey.api_key)
                setKeyCopied(true)
                Toast.show({type: 'success', text1: 'API key copied to clipboard.'})
                setTimeout(() => setKeyCopied(false), 3000)
            } catch {
                Toast.show({type: 'error', text1: 'Failed to copy key.'})
            }
        }
    }

    const openRevokeModal = (keyId: string, keyPreview: string) => {
        setRevokeKeyId(keyId)
        setRevokeKeyPreview(keyPreview)
        setIsRevokeModalOpen(true)
    }

    const closeRevokeModal = () => {
        setRevokeKeyId(null)
        setRevokeKeyPreview('')
        setIsRevokeModalOpen(false)
    }

    const confirmRevoke = async () => {
        if (!revokeKeyId) return
        try {
            await API.post('CorpOnboarding', `/partner-api-key/${revokeKeyId}/revoke`, {})
            Toast.show({type: 'success', text1: 'API key revoked successfully.'})
            fetchApiKeys()
        } catch (error: any) {
            console.error('Error revoking API key:', error)
            Toast.show({type: 'error', text1: error?.message || 'Failed to revoke API key. Please try again.'})
        } finally {
            closeRevokeModal()
        }
    }

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '—'
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const formatScope = (scope: string) => {
        if (scope === 'read_write') return 'Read & Write'
        return scope.charAt(0).toUpperCase() + scope.slice(1)
    }

    const handleSort = (field: 'scope' | 'status' | 'created' | 'last_used') => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const sortedKeys = [...apiKeys].sort((a, b) => {
        if (!sortField) return 0
        const dir = sortDirection === 'asc' ? 1 : -1
        switch (sortField) {
            case 'scope':
                return dir * a.scope.localeCompare(b.scope)
            case 'status':
                return dir * (Number(b.is_active) - Number(a.is_active))
            case 'created':
                return dir * (new Date(a.create_time).getTime() - new Date(b.create_time).getTime())
            case 'last_used': {
                const aTime = a.last_used ? new Date(a.last_used).getTime() : 0
                const bTime = b.last_used ? new Date(b.last_used).getTime() : 0
                return dir * (aTime - bTime)
            }
            default:
                return 0
        }
    })

    const activeCount = apiKeys.filter(k => k.is_active).length
    const revokedCount = apiKeys.filter(k => !k.is_active).length

    return (
        <>
            <ConfirmationModal
                open={isRevokeModalOpen}
                onClose={closeRevokeModal}
                onConfirm={confirmRevoke}
                title="Revoke API Key"
                description={`Are you sure you want to revoke the API key: ${revokeKeyPreview}? This action cannot be undone.`}
            />

            <Box sx={{mb: 16, mt: '20px', px: {xs: 4, md: 8}}}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 5,
                }}>
                    <Box>
                        <Typography variant="inherit" sx={{
                            fontFamily: font.bold,
                            fontSize: '1.46rem',
                            color: 'text.primary',
                            lineHeight: 1.3,
                        }}>
                            API Keys
                        </Typography>
                        <Typography variant="inherit" sx={{
                            fontFamily: font.regular,
                            fontSize: '0.975rem',
                            color: 'text.secondary',
                            mt: 0.5,
                        }}>
                            Manage partner API keys across all corporate codes.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon sx={{fontSize: '18px !important'}}/>}
                        onClick={() => setGenerateDialogOpen(true)}
                        sx={{
                            px: 3,
                            py: 1,
                            fontSize: '0.975rem',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(66, 107, 240, 0.3)',
                        }}
                    >
                        Generate Key
                    </Button>
                </Box>

                {/* Stats + Filter Row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}>
                    <Stack direction="row" spacing={1.5}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: '#ECFFF1',
                            border: '1px solid #3DA010',
                            borderRadius: '20px',
                            px: 2,
                            py: 0.5,
                        }}>
                            <Box sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                backgroundColor: '#3DA010',
                            }}/>
                            <Typography variant="inherit" sx={{
                                fontFamily: font.semibold,
                                fontSize: '0.89rem',
                                color: '#005418',
                            }}>
                                {activeCount} Active
                            </Typography>
                        </Box>
                        {includeRevoked && revokedCount > 0 && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: '#FFF3F0',
                                border: '1px solid #FF3D00',
                                borderRadius: '20px',
                                px: 2,
                                py: 0.5,
                            }}>
                                <Box sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    backgroundColor: '#FF3D00',
                                }}/>
                                <Typography variant="inherit" sx={{
                                    fontFamily: font.semibold,
                                    fontSize: '0.89rem',
                                    color: '#541400',
                                }}>
                                    {revokedCount} Revoked
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={includeRevoked}
                                onChange={(e) => setIncludeRevoked(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="inherit" sx={{
                                fontFamily: font.medium,
                                fontSize: '0.89rem',
                                color: 'text.secondary',
                            }}>
                                Show revoked
                            </Typography>
                        }
                        sx={{mr: 0}}
                    />
                </Box>

                {/* Table */}
                <Loader loading={isLoading}>
                    {apiKeys.length === 0 ? (
                        <Box sx={{
                            py: 12,
                            textAlign: 'center',
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: '12px',
                            backgroundColor: '#FAFAFA',
                        }}>
                            <KeyOffOutlinedIcon sx={{fontSize: 44, color: 'grey.300', mb: 2}}/>
                            <Typography sx={{
                                fontFamily: font.semibold,
                                fontSize: '1.3rem',
                                color: 'text.primary',
                                mb: 1,
                            }}>
                                No API keys yet
                            </Typography>
                            <Typography sx={{
                                fontFamily: font.regular,
                                fontSize: '1.06rem',
                                color: 'text.secondary',
                            }}>
                                Click "Generate Key" to create your first API key.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                borderRadius: '12px',
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                            }}
                        >
                            <Table size="small" sx={{tableLayout: 'fixed'}}>
                                <colgroup>
                                    <col style={{width: '17%'}}/>
                                    <col style={{width: '30%'}}/>
                                    <col style={{width: '11%'}}/>
                                    <col style={{width: '9%'}}/>
                                    <col style={{width: '12%'}}/>
                                    <col style={{width: '12%'}}/>
                                    <col style={{width: '9%'}}/>
                                </colgroup>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: '#F9F9FB'}}>
                                        {([
                                            {label: 'Corp Code', sortKey: null},
                                            {label: 'Key Preview', sortKey: null},
                                            {label: 'Scope', sortKey: 'scope' as const},
                                            {label: 'Status', sortKey: 'status' as const},
                                            {label: 'Created', sortKey: 'created' as const},
                                            {label: 'Last Used', sortKey: 'last_used' as const},
                                            {label: '', sortKey: null},
                                        ]).map((col, i) => (
                                            <TableCell
                                                key={i}
                                                sortDirection={sortField === col.sortKey ? sortDirection : false}
                                                sx={{
                                                    fontFamily: font.semibold,
                                                    fontSize: '0.89rem',
                                                    color: '#919191',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.08em',
                                                    py: 2,
                                                    px: 2.5,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                {col.sortKey ? (
                                                    <TableSortLabel
                                                        active={sortField === col.sortKey}
                                                        direction={sortField === col.sortKey ? sortDirection : 'asc'}
                                                        onClick={() => handleSort(col.sortKey!)}
                                                        sx={{
                                                            color: '#919191 !important',
                                                            '&.Mui-active': {color: '#426BF0 !important'},
                                                            '& .MuiTableSortLabel-icon': {
                                                                fontSize: '1.14rem',
                                                                color: '#426BF0 !important',
                                                            },
                                                        }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                ) : col.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedKeys.map((key, idx) => (
                                        <TableRow
                                            key={key.id}
                                            sx={{
                                                '&:hover': {backgroundColor: '#FAFAFF'},
                                                '&:last-child td': {borderBottom: 0},
                                                opacity: key.is_active ? 1 : 0.6,
                                            }}
                                        >
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Tooltip title={key.corp_code} placement="top-start" componentsProps={{tooltip: {sx: {fontFamily: font.medium, fontSize: '0.75rem'}}}}>
                                                    <Typography variant="inherit" sx={{
                                                        fontFamily: font.semibold,
                                                        fontSize: '0.975rem',
                                                        color: 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {key.corp_code}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Box
                                                    component="code"
                                                    sx={{
                                                        fontFamily: font.mono,
                                                        fontSize: '0.89rem',
                                                        color: '#572086',
                                                        backgroundColor: '#F8F4FE',
                                                        px: 2,
                                                        py: 0.75,
                                                        borderRadius: '4px',
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        letterSpacing: '0.02em',
                                                    }}
                                                >
                                                    {key.api_key_preview}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Chip
                                                    label={formatScope(key.scope)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily: font.medium,
                                                        fontSize: '0.89rem',
                                                        backgroundColor: key.scope === 'read_write' ? '#FAE8F8' : '#ECF0FE',
                                                        color: key.scope === 'read_write' ? '#7B116F' : '#426BF0',
                                                        height: 28,
                                                        borderRadius: '4px',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Box sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}>
                                                    <Box sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: key.is_active ? '#3DA010' : '#FF3D00',
                                                        flexShrink: 0,
                                                    }}/>
                                                    <Typography variant="inherit" sx={{
                                                        fontFamily: font.medium,
                                                        fontSize: '0.89rem',
                                                        color: key.is_active ? '#005418' : '#541400',
                                                    }}>
                                                        {key.is_active ? 'Active' : 'Revoked'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Typography variant="inherit" sx={{
                                                    fontFamily: font.regular,
                                                    fontSize: '0.89rem',
                                                    color: 'text.primary',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {formatDate(key.create_time)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                <Typography variant="inherit" sx={{
                                                    fontFamily: font.regular,
                                                    fontSize: '0.89rem',
                                                    color: key.last_used ? 'text.primary' : 'text.disabled',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {formatDate(key.last_used)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{py: 1.5, px: 2.5, borderColor: 'divider'}}>
                                                {key.is_active ? (
                                                    <Button
                                                        size="small"
                                                        onClick={() => openRevokeModal(key.id, key.api_key_preview)}
                                                        sx={{
                                                            fontFamily: font.semibold,
                                                            fontSize: '0.89rem',
                                                            color: '#FF3D00',
                                                            backgroundColor: '#FFF3F0',
                                                            borderRadius: '6px',
                                                            px: 2.5,
                                                            py: 0.75,
                                                            minWidth: 0,
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                backgroundColor: '#FFE5DE',
                                                            },
                                                        }}
                                                    >
                                                        Revoke
                                                    </Button>
                                                ) : (
                                                    <Typography variant="inherit" sx={{
                                                        fontFamily: font.regular,
                                                        fontSize: '0.89rem',
                                                        color: 'text.disabled',
                                                    }}>
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Loader>
            </Box>

            {/* Generate Dialog */}
            <Dialog
                open={generateDialogOpen}
                onClose={handleCloseGenerateDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{sx: {borderRadius: '12px', p: 1}}}
            >
                <DialogTitle sx={{
                    fontFamily: font.bold,
                    fontSize: '1.22rem',
                    color: 'text.primary',
                    pb: 0,
                    pt: 2.5,
                    px: 3,
                }}>
                    {generatedKey ? 'API Key Generated' : 'Generate New API Key'}
                </DialogTitle>
                <DialogContent sx={{px: 3}}>
                    {generatedKey ? (
                        <Box sx={{mt: 2}}>
                            <Alert
                                severity="warning"
                                sx={{
                                    mb: 2.5,
                                    borderRadius: '8px',
                                    fontFamily: font.regular,
                                    fontSize: '0.89rem',
                                    py: 0.5,
                                    '& .MuiAlert-message': {fontFamily: font.regular, fontSize: '0.6875rem'},
                                    '& .MuiAlert-icon': {fontSize: '1.3rem', mr: 1},
                                }}
                            >
                                <strong>Store this key securely.</strong> It will not be shown again.
                            </Alert>
                            <Stack spacing={1.5} sx={{mb: 2.5}}>
                                {[
                                    {label: 'Corp Code', value: generatedKey.corp_code},
                                    {label: 'Scope', value: formatScope(generatedKey.scope)},
                                    {label: 'Preview', value: generatedKey.api_key_preview, mono: true},
                                ].map((item) => (
                                    <Box key={item.label} sx={{display: 'flex', alignItems: 'center'}}>
                                        <Typography variant="inherit" sx={{
                                            fontFamily: font.semibold,
                                            fontSize: '0.81rem',
                                            color: '#919191',
                                            width: 70,
                                            flexShrink: 0,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                        }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="inherit" sx={{
                                            fontFamily: item.mono ? font.mono : font.medium,
                                            fontSize: '0.89rem',
                                            color: item.mono ? '#572086' : 'text.primary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            ...(item.mono && {
                                                backgroundColor: '#F8F4FE',
                                                px: 1.5,
                                                py: 0.25,
                                                borderRadius: '4px',
                                            }),
                                        }}>
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Box sx={{
                                p: 2,
                                backgroundColor: '#F9F9FB',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                            }}>
                                <Typography variant="inherit" sx={{
                                    fontFamily: font.mono,
                                    wordBreak: 'break-all',
                                    flexGrow: 1,
                                    fontSize: '0.89rem',
                                    color: 'text.primary',
                                    lineHeight: 1.6,
                                    letterSpacing: '0.02em',
                                }}>
                                    {generatedKey.api_key}
                                </Typography>
                                <Tooltip title={keyCopied ? 'Copied!' : 'Copy'} componentsProps={{tooltip: {sx: {fontFamily: font.medium, fontSize: '0.6875rem'}}}}>
                                    <IconButton
                                        onClick={handleCopyKey}
                                        size="small"
                                        sx={{
                                            color: keyCopied ? 'success.main' : 'primary.main',
                                            backgroundColor: keyCopied ? '#ECFFF1' : '#ECF0FE',
                                            borderRadius: '6px',
                                            flexShrink: 0,
                                            p: 0.75,
                                            '&:hover': {
                                                backgroundColor: keyCopied ? '#D4F5DD' : '#D9E1FC',
                                            },
                                        }}
                                    >
                                        <ContentCopyIcon sx={{fontSize: 14}}/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{mt: 2}}>
                            <Autocomplete
                                options={corpCodes}
                                value={generateCorpCode}
                                onChange={(_event, newValue) => setGenerateCorpCode(newValue)}
                                loading={isCorpCodesLoading}
                                size="small"
                                sx={{
                                    mb: 2.5,
                                    '& .MuiInputLabel-root': {fontFamily: font.medium, fontSize: '0.89rem'},
                                    '& .MuiInputBase-input': {fontFamily: font.regular, fontSize: '0.89rem'},
                                    '& .MuiOutlinedInput-root': {fontFamily: font.regular, fontSize: '0.89rem'},
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Corporate Code"
                                        placeholder="Search corporate codes..."
                                        variant="outlined"
                                        required
                                    />
                                )}
                            />
                            <FormControl fullWidth size="small" sx={{
                                '& .MuiInputLabel-root': {fontFamily: font.medium, fontSize: '0.89rem'},
                                '& .MuiSelect-select': {fontFamily: font.regular, fontSize: '0.89rem'},
                            }}>
                                <InputLabel id="scope-label">Scope</InputLabel>
                                <Select
                                    labelId="scope-label"
                                    value={generateScope}
                                    label="Scope"
                                    onChange={(e) => setGenerateScope(e.target.value)}
                                >
                                    <MenuItem value="read" sx={{fontFamily: font.regular, fontSize: '0.89rem'}}>Read</MenuItem>
                                    <MenuItem value="read_write" sx={{fontFamily: font.regular, fontSize: '0.89rem'}}>Read & Write</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5, pt: 0.5}}>
                    <Button
                        onClick={handleCloseGenerateDialog}
                        sx={{
                            fontFamily: font.medium,
                            fontSize: '0.975rem',
                            color: 'text.secondary',
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            px: 2.5,
                            py: 0.75,
                            '&:hover': {backgroundColor: '#F5F5F5'},
                        }}
                    >
                        {generatedKey ? 'Close' : 'Cancel'}
                    </Button>
                    {!generatedKey && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerateKey}
                            disabled={isGenerating || !generateCorpCode}
                            sx={{
                                px: 3,
                                py: 0.75,
                                fontSize: '0.975rem',
                                fontFamily: font.medium,
                                textTransform: 'none',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(66, 107, 240, 0.3)',
                            }}
                        >
                            {isGenerating ? 'Generating...' : 'Generate'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ApiKeysManagement